// small-lakes.json: lakes smaller than a hex that matter for reading the map (Dead Sea, Sea of
// Galilee, Lake Van, Lake Urmia ...). A point and an area each, with the sources that say so. The
// view draws them as water decals, never as hexes. The file is optional: without it the map has
// no decals, and an entry that cannot be used is skipped with a warning.

import type { SourceId } from '../core/schema.ts';
import { makeReader } from './fetch.ts';
import type { Fetcher } from './types.ts';

const FILE = 'small-lakes.json';
const LAT_MAX = 90;
const LON_MAX = 180;

/** A lake without a hex of its own. */
export interface SmallLake {
  name: string;
  lat: number;
  lon: number;
  area_km2: number;
  sources: SourceId[];
}

const isText = (v: unknown): v is string => typeof v === 'string' && v !== '';
const isNumber = (v: unknown, limit: number): v is number => typeof v === 'number' && Math.abs(v) <= limit;

/** Why an entry cannot be used, or null. A lake without sources would cite nothing for its decal. */
function problemOf(e: Partial<Record<keyof SmallLake, unknown>> | null): string | null {
  if (!e || !isText(e.name)) {
    return 'has no name';
  }
  if (!isNumber(e.lat, LAT_MAX) || !isNumber(e.lon, LON_MAX)) {
    return 'has no valid position';
  }
  if (typeof e.area_km2 !== 'number' || !(e.area_km2 > 0) || !Number.isFinite(e.area_km2)) {
    return 'has no positive area';
  }
  if (!Array.isArray(e.sources) || e.sources.length === 0 || !e.sources.every(isText)) {
    return 'has no sources';
  }
  return null;
}

/** The usable lakes of a parsed small-lakes.json, and one message per entry left out. */
export function parseSmallLakes(raw: unknown): { lakes: SmallLake[]; problems: string[] } {
  if (!Array.isArray(raw)) {
    return { lakes: [], problems: [`data: ${FILE} is not a list`] };
  }

  const lakes: SmallLake[] = [];
  const problems: string[] = [];
  for (const entry of raw as Array<Partial<Record<keyof SmallLake, unknown>> | null>) {
    const why = problemOf(entry);
    if (why) {
      problems.push(`data: ${FILE} has an entry (${String(entry?.name)}) that ${why}`);
      continue;
    }
    lakes.push(entry as unknown as SmallLake); // passed problemOf
  }
  return { lakes, problems };
}

/** Fetch small-lakes.json from `baseUrl` ('/data'); empty (with a warning) when missing or unusable. */
export async function loadSmallLakes(baseUrl: string, fetcher?: Fetcher): Promise<SmallLake[]> {
  const reader = makeReader(baseUrl, fetcher);
  const raw = await reader.json<unknown>(FILE);
  if (raw === null) {
    return [];
  }

  const { lakes, problems } = parseSmallLakes(raw);
  for (const msg of problems) {
    console.warn(msg);
  }
  return lakes;
}
